import { sfQuery } from "../../API";

// Helper function to group data by owner
const groupByOwner = (
  data,
  ownerField = "Staging_Site_Creation_Assigned_To__r.Name"
) => {
  const breakdown = {};
  data.forEach((item) => {
    const ownerName = getNestedValue(item, ownerField) || "Unknown";
    breakdown[ownerName] = (breakdown[ownerName] || 0) + 1;
  });
  return breakdown;
};

// Helper function to get nested object values
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

// Helper function to get current month name in format "January-2025"
const getCurrentMonthName = () => {
  const now = new Date();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthName = monthNames[now.getMonth()];
  const year = now.getFullYear();
  return `${monthName}-${year}`;
};

// Helper function to group data by content status
const groupByContentStatus = (data, statusField = "Content_Status__c") => {
  const breakdown = {};
  data.forEach((item) => {
    const status = getNestedValue(item, statusField) || "Unknown";
    breakdown[status] = (breakdown[status] || 0) + 1;
  });
  return breakdown;
};

// Get list of fulfillment team users for the filter dropdown
export const getUsersList = async (context) => {
  const users = await sfQuery(
    "User",
    "Id, Name",
    "IsActive = true AND (UserRole.Name LIKE '%Social Media%' OR UserRole.Name LIKE '%Website%' OR UserRole.Name LIKE '%SEO%' OR UserRole.Name LIKE '%Paid Ads%')",
    context
  );
  return users.map((user) => ({ id: user.Id, name: user.Name }));
};

// Get open tasks for fulfillment team members
export const getOpenTasks = async (context, selectedUser = null) => {
  let whereClause = `(NOT Subject LIKE 'Unresolved Email:%') AND Status = 'Open' AND ActivityDate <= TODAY AND (Owner.UserRole.Name LIKE '%Social Media%' OR Owner.UserRole.Name LIKE '%Website%' OR Owner.UserRole.Name LIKE '%SEO%' OR Owner.UserRole.Name LIKE '%Paid Ads%')`;

  if (selectedUser) {
    whereClause += ` AND OwnerId = '${selectedUser}' AND`;
  }

  const result = await sfQuery(
    "Task",
    "Id, Subject, Owner.Name, What.Name, ActivityDate, Task_Type__c, Description, WhatId, OwnerId",
    whereClause,
    context
  );

  return result.map((task) => ({
    id: task.Id,
    subject: task.Subject || "No Subject",
    owner: task.Owner?.Name || "Unknown",
    account: task.What?.Name || "No Account",
    date: task.ActivityDate,
    taskType: task.Task_Type__c || "General",
    description: task.Description || "",
    accountId: task.WhatId,
    ownerId: task.OwnerId,
  }));
};

// 1) Websites Launched This Month
export const getWebsitesLaunchedThisMonth = async (
  context,
  selectedUser = null
) => {
  let whereClause = `Staging_Site_Status__c = 'Launched' AND Launch_Due_Date__c = THIS_MONTH`;

  if (selectedUser) {
    whereClause += ` AND Staging_Site_Creation_Assigned_To__c = '${selectedUser}'`;
  }

  const result = await sfQuery(
    "Staging_Site_Build__c",
    "Id, Name, Staging_Site_Status__c, Launch_Due_Date__c, Staging_Site_Creation_Assigned_To__c, Staging_Site_Creation_Assigned_To__r.Name",
    whereClause,
    context
  );

  const response = { count: result.length, data: result };

  // Always add breakdown by owner for better visibility
  response.breakdown = groupByOwner(
    result,
    "Staging_Site_Creation_Assigned_To__r.Name"
  );

  return response;
};

// 2) Websites In Progress
export const getWebsitesInProgress = async (context, selectedUser = null) => {
  let whereClause = `(NOT Staging_Site_Status__c IN ('Launched', 'Pending Onboarding')) AND Website_Information__r.Account__r.Membership_Status__c = 'Activated'`;

  if (selectedUser) {
    whereClause += ` AND Staging_Site_Creation_Assigned_To__c = '${selectedUser}'`;
  }

  const result = await sfQuery(
    "Staging_Site_Build__c",
    "Id, Name, Staging_Site_Status__c, Launch_Due_Date__c, Staging_Site_Creation_Assigned_To__c, Staging_Site_Creation_Assigned_To__r.Name",
    whereClause,
    context
  );

  const response = { count: result.length, data: result };

  // Always add breakdown by owner for better visibility
  response.breakdown = groupByOwner(
    result,
    "Staging_Site_Creation_Assigned_To__r.Name"
  );

  return response;
};

// 3) Websites with Roadblocks
export const getWebsitesWithRoadblocks = async (
  context,
  selectedUser = null
) => {
  let whereClause = `Staging_Site_Status__c = 'Waiting Client Info/Roadblock' AND Website_Information__r.Account__r.Membership_Status__c = 'Activated'`;

  if (selectedUser) {
    whereClause += ` AND Staging_Site_Creation_Assigned_To__c = '${selectedUser}'`;
  }

  const result = await sfQuery(
    "Staging_Site_Build__c",
    "Id, Name, Staging_Site_Status__c, Launch_Due_Date__c, Staging_Site_Creation_Assigned_To__c, Staging_Site_Creation_Assigned_To__r.Name",
    whereClause,
    context
  );

  const response = { count: result.length, data: result };

  // Always add breakdown by owner for better visibility
  response.breakdown = groupByOwner(
    result,
    "Staging_Site_Creation_Assigned_To__r.Name"
  );

  return response;
};

// 4) Website Updates This Month
export const getWebsiteUpdatesThisMonth = async (
  context,
  selectedUser = null
) => {
  let whereClause = `Website_Update_Status__c = 'Completed' AND Update_Completed_Date__c = THIS_MONTH`;

  if (selectedUser) {
    whereClause += ` AND Update_Completed_By__c = '${selectedUser}'`;
  }

  const result = await sfQuery(
    "Website_Update__c",
    "Id, Website_Update_Status__c, Update_Completed_Date__c, Update_Completed_By__c, Update_Completed_By__r.Name",
    whereClause,
    context
  );

  const response = { count: result.length, data: result };

  // Always add breakdown by completed by user for better visibility
  response.breakdown = groupByOwner(result, "Update_Completed_By__r.Name");

  return response;
};

// 5) Social Media Posts This Month
export const getSocialMediaPostsThisMonth = async (
  context,
  selectedUser = null
) => {
  let whereClause = `Post_Completed_Date__c = THIS_MONTH`;

  if (selectedUser) {
    whereClause += ` AND Social_Poster__c = '${selectedUser}'`;
  }

  const result = await sfQuery(
    "Social_Media_Post__c",
    "Id, Post_Completed_Date__c, Social_Poster__c, Social_Poster__r.Name",
    whereClause,
    context
  );

  const response = { count: result.length, data: result };

  // Always add breakdown by social poster for better visibility
  response.breakdown = groupByOwner(result, "Social_Poster__r.Name");

  return response;
};

// 6) Social Media Posts - Overdue
export const getSocialMediaPostsOverdue = async (
  context,
  selectedUser = null
) => {
  let whereClause = `Hold_Up__c != 'Cancelled' AND Hold_Up__c != null AND Date_of_post__c = THIS_YEAR AND Date_of_Post__c < TODAY AND Post_Completed_Date__c = null AND Social_Media__r.Account__r.Membership_Status__c = 'Activated' AND Social_Media__r.Social_Media_Status__c = 'Active - Posting'`;

  if (selectedUser) {
    whereClause += ` AND Social_Poster__c = '${selectedUser}'`;
  }

  const result = await sfQuery(
    "Social_Media_Post__c",
    "Id, Date_of_Post__c, Post_Completed_Date__c, Social_Poster__c, Social_Poster__r.Name",
    whereClause,
    context
  );

  const response = { count: result.length, data: result };

  // Always add breakdown by social poster for better visibility
  response.breakdown = groupByOwner(result, "Social_Poster__r.Name");

  return response;
};

// 7) PPC + Bing - Roadblocks
export const getPPCBingRoadblocks = async (context, selectedUser = null) => {
  // Query for PPC roadblocks
  let ppcWhereClause = `PPC_Status__c = 'Roadblock' AND Account__r.Membership_Status__c = 'Activated'`;
  if (selectedUser) {
    ppcWhereClause += ` AND Account__r.PPC_Specialist__c = '${selectedUser}'`;
  }

  const ppcResults = await sfQuery(
    "PPC__c",
    "Id, PPC_Status__c, Account__c, Account__r.PPC_Specialist__c, Account__r.PPC_Specialist__r.Name",
    ppcWhereClause,
    context
  );

  // Query for Bing roadblocks
  let bingWhereClause = `BING_Status__c = 'Roadblock' AND Account__r.Membership_Status__c = 'Activated'`;
  if (selectedUser) {
    bingWhereClause += ` AND Account__r.BING_Specialist__c = '${selectedUser}'`;
  }

  const bingResults = await sfQuery(
    "BING__c",
    "Id, BING_Status__c, Account__c, Account__r.BING_Specialist__c, Account__r.BING_Specialist__r.Name",
    bingWhereClause,
    context
  );

  const totalCount = ppcResults.length + bingResults.length;

  const response = {
    count: totalCount,
    data: {
      ppc: ppcResults,
      bing: bingResults,
    },
    breakdown: {
      PPC: ppcResults.length,
      Bing: bingResults.length,
    },
  };

  // Add breakdown by user if no specific user is selected
  if (!selectedUser) {
    const ppcBreakdown = groupByOwner(
      ppcResults,
      "Account__r.PPC_Specialist__r.Name"
    );
    const bingBreakdown = groupByOwner(
      bingResults,
      "Account__r.BING_Specialist__r.Name"
    );

    response.userBreakdown = {
      ppc: ppcBreakdown,
      bing: bingBreakdown,
    };
  }

  return response;
};

// 8) Social+ + Nextdoor - Roadblocks
export const getSocialPlusNextdoorRoadblocks = async (
  context,
  selectedUser = null
) => {
  // Query for Social+ roadblocks (using PPC table with Account_Status__c)
  let socialPlusWhereClause = `Account_Status__c = 'Roadblock' AND Account__r.Membership_Status__c = 'Activated'`;
  if (selectedUser) {
    socialPlusWhereClause += ` AND Account__r.Social_Ad_Specialist__c = '${selectedUser}'`;
  }

  const socialPlusResults = await sfQuery(
    "Social_Plus__c",
    "Id, Account_Status__c, Account__c, Account__r.Social_Ad_Specialist__c, Account__r.Social_Ad_Specialist__r.Name",
    socialPlusWhereClause,
    context
  );

  // Query for Nextdoor roadblocks (using BING table with Account_Status__c)
  let nextdoorWhereClause = `BING_Status__c = 'Roadblock' AND Account__r.Membership_Status__c = 'Activated'`;
  if (selectedUser) {
    nextdoorWhereClause += ` AND Account__r.Nextdoor_Ad_Specialist__c = '${selectedUser}'`;
  }

  const nextdoorResults = await sfQuery(
    "BING__c",
    "Id, BING_Status__c, Account__c, Account__r.Nextdoor_Ad_Specialist__c, Account__r.Nextdoor_Ad_Specialist__r.Name",
    nextdoorWhereClause,
    context
  );

  const totalCount = socialPlusResults.length + nextdoorResults.length;

  const response = {
    count: totalCount,
    data: {
      socialPlus: socialPlusResults,
      nextdoor: nextdoorResults,
    },
    breakdown: {
      "Social+": socialPlusResults.length,
      Nextdoor: nextdoorResults.length,
    },
  };

  // Add breakdown by user if no specific user is selected
  if (!selectedUser) {
    const socialPlusBreakdown = groupByOwner(
      socialPlusResults,
      "Account__r.Social_Ad_Specialist__r.Name"
    );
    const nextdoorBreakdown = groupByOwner(
      nextdoorResults,
      "Account__r.Nextdoor_Ad_Specialist__r.Name"
    );

    response.userBreakdown = {
      socialPlus: socialPlusBreakdown,
      nextdoor: nextdoorBreakdown,
    };
  }

  return response;
};

// 9) Content Uploaded This Month
export const getContentUploadedThisMonth = async (
  context,
  selectedUser = null
) => {
  let whereClause = `ActivityDate = THIS_MONTH AND Status = 'Completed' AND Subject LIKE 'Upload Content'`;

  if (selectedUser) {
    whereClause += ` AND OwnerId = '${selectedUser}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, Subject, Owner.Name, ActivityDate, Status, OwnerId",
    whereClause,
    context
  );

  const response = { count: result.length, data: result };

  // Always add breakdown by owner for better visibility
  response.breakdown = groupByOwner(result, "Owner.Name");

  return response;
};

// 10) Content Completed This Month
export const getContentCompletedThisMonth = async (
  context,
  selectedUser = null
) => {
  let whereClause = `ActivityDate = THIS_MONTH AND Status = 'Completed' AND Owner.UserRole.Name = 'Content Writer in Core Products'`;

  if (selectedUser) {
    whereClause += ` AND OwnerId = '${selectedUser}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, Subject, Owner.Name, ActivityDate, Status, OwnerId",
    whereClause,
    context
  );

  const response = { count: result.length, data: result };

  // Always add breakdown by owner for better visibility
  response.breakdown = groupByOwner(result, "Owner.Name");

  return response;
};

// 11) Content Orders Status
export const getContentOrdersStatus = async (context, selectedUser = null) => {
  const currentMonthName = getCurrentMonthName();
  let whereClause = `SEO_Content__r.Account__r.Membership_Status__c = 'Activated' AND Name = '${currentMonthName}' AND (NOT SEO_Content__r.Account__r.Name LIKE '%test %')`;

  // Note: selectedUser filtering would need to be implemented based on a user field in Write_Content__c
  // if (selectedUser) {
  //   whereClause += ` AND SomeUserField__c = '${selectedUser}'`;
  // }

  const result = await sfQuery(
    "SEO_Write_Content__c",
    "Id, Name, Content_Status__c, SEO_Content__r.Account__r.Name, SEO_Content__r.Account__r.Membership_Status__c",
    whereClause,
    context
  );

  const response = { count: result.length, data: result };

  // Breakdown by Content_Status__c
  response.breakdown = groupByContentStatus(result, "Content_Status__c");

  return response;
};

// 12) Content Orders Overdue
export const getContentOrdersOverdue = async (context, selectedUser = null) => {
  let whereClause = `SEO_Content__r.Account__r.Membership_Status__c = 'Activated' AND Content_Due_Date__c < TODAY AND (NOT SEO_Content__r.Account__r.Name LIKE '%test %') AND Content_Status__c != 'Completed'`;

  // Note: selectedUser filtering would need to be implemented based on a user field in Write_Content__c
  // if (selectedUser) {
  //   whereClause += ` AND SomeUserField__c = '${selectedUser}'`;
  // }

  const result = await sfQuery(
    "SEO_Write_Content__c",
    "Id, Name, Content_Status__c, Content_Due_Date__c, SEO_Content__r.Account__r.Name, SEO_Content__r.Account__r.Membership_Status__c",
    whereClause,
    context
  );

  const response = { count: result.length, data: result };

  // Breakdown by Content_Status__c for overdue items
  response.breakdown = groupByContentStatus(result, "Content_Status__c");

  return response;
};
